import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { templatesApi } from '@/services/templates';
import type { CreateVersionPayload, TestPayload } from '@/types/template';

export const templateKeys = {
  all: ['templates'] as const,
  detail: (id: string) => [...templateKeys.all, 'detail', id] as const,
};

export function useTemplates() {
  return useQuery({
    queryKey: templateKeys.all,
    queryFn: () => templatesApi.list(),
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: templateKeys.detail(id ?? ''),
    queryFn: () => templatesApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateTemplateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateVersionPayload }) =>
      templatesApi.createVersion(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: templateKeys.detail(id) });
    },
  });
}

export function useActivateTemplateVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionId }: { id: string; versionId: string }) =>
      templatesApi.activateVersion(id, versionId),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: templateKeys.detail(id) });
      qc.invalidateQueries({ queryKey: templateKeys.all });
    },
  });
}

export function useTestTemplate() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TestPayload }) =>
      templatesApi.test(id, payload),
  });
}
